@if (hasNoCampaign()) {
  <sa-ads-empty-state (createCampaign)="goToBuilder()" (browseVideos)="goToVideos()" />
} @else if (reportingState() === 'error') {
  <div
    role="alert"
    class="flex flex-col items-start gap-y-3 rounded-xl border border-destructive/30 bg-destructive/5 p-5"
  >
    <p class="m-0 text-sm font-semibold text-error">We could not load your ads performance</p>
    <p class="m-0 text-sm text-secondary">{{ reportingError() }}</p>
    <button type="button" (click)="reload()" class="secondary-button">Try again</button>
  </div>
} @else {
  <section class="flex w-full flex-col gap-y-5">
    <!-- Header -->
    <header class="flex flex-col gap-y-4 lg:flex-row lg:items-end lg:justify-between lg:gap-x-6">
      <div class="flex flex-col gap-y-1">
        <p class="m-0 text-xs font-bold tracking-[0.08em] text-brand-studio uppercase">
          Spark Ads · TikTok
        </p>
        <h1 class="m-0 text-2xl font-bold text-primary">Your ads are working.</h1>
        @if (summaryLine()) {
          <p class="m-0 text-sm text-secondary">{{ summaryLine() }}</p>
        }
      </div>

      <div class="flex shrink-0 flex-row items-center gap-x-3">
        <nav
          aria-label="Date range"
          class="flex flex-row items-center gap-x-1 rounded-full border border-border bg-card p-1"
        >
          @for (preset of rangePresets; track preset.key) {
            <button
              type="button"
              (click)="selectRange(preset.key)"
              [attr.aria-current]="store.rangePreset() === preset.key ? 'true' : null"
              class="cursor-pointer rounded-full px-3 py-1.5 text-sm font-semibold transition-colors"
              [class]="getRangeClass(preset.key)"
            >
              {{ preset.label }}
            </button>
          }
        </nav>

        <button type="button" (click)="goToBuilder()" class="primary-button">+ New Spark ad</button>
      </div>
    </header>

    <!-- KPI row -->
    @if (reportingState() === 'loading') {
      <div
        aria-busy="true"
        aria-label="Loading performance summary"
        class="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5"
      >
        @for (card of [1, 2, 3, 4, 5]; track card) {
          <div class="flex flex-col gap-y-2 rounded-xl border border-border bg-card p-4 shadow-sm">
            <div class="h-3 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-white/10"></div>
            <div class="h-7 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-white/10"></div>
            <div class="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-white/10"></div>
          </div>
        }
      </div>
    } @else if (kpis().length > 0) {
      <dl class="m-0 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        @for (kpi of kpis(); track kpi.key) {
          <div class="flex flex-col gap-y-1.5 rounded-xl border border-border bg-card p-4 shadow-sm">
            <dt class="text-xs font-bold tracking-[0.08em] text-secondary uppercase">
              {{ kpi.label }}
            </dt>
            <dd class="m-0 text-2xl font-bold tabular-nums text-primary">{{ kpiValue(kpi) }}</dd>
            <div class="flex flex-row items-center gap-x-2">
              @if (kpi.deltaPercent !== null) {
                <p class="m-0" [class]="kpiDeltaClass(kpi)">{{ kpiDelta(kpi) }}</p>
                <p class="m-0 text-xs text-secondary">vs prior period</p>
              } @else {
                <p class="m-0 text-xs text-secondary">No prior period to compare</p>
              }
            </div>
          </div>
        }
      </dl>
    }

    <!-- Body: chart + tree, with the rail alongside on wide screens -->
    <div class="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div class="flex min-w-0 flex-1 flex-col gap-5">
        <sa-daily-performance-chart
          [series]="series()"
          [metric]="store.chartMetric()"
          [loadState]="seriesState()"
          [currencyCode]="currencyCode()"
          (metricChange)="selectMetric($event)"
        />

        <sa-campaign-structure-tree [structure]="structure()" [loadState]="structureState()" />
      </div>

      <aside class="flex w-full shrink-0 flex-col gap-5 xl:w-80">
        <!-- Top creators -->
        @if (topCreators().length > 0) {
          <article class="flex flex-col gap-y-4 rounded-xl border border-border bg-card p-5 shadow-sm">
            <div class="flex flex-row items-baseline justify-between">
              <h2 class="m-0 text-base font-semibold text-primary">Top creators</h2>
              <p class="m-0 text-xs font-semibold text-secondary">by ROAS</p>
            </div>

            <ul class="m-0 flex list-none flex-col gap-y-3 p-0">
              @for (creator of topCreators(); track creator.videoIds[0] ?? creator.handle) {
                <li class="flex flex-row items-center gap-x-3">
                  <div class="flex min-w-0 flex-1 flex-col gap-y-0.5">
                    <p class="m-0 truncate text-sm font-bold text-primary">
                      {{ creator.handle ?? 'Unknown creator' }}
                    </p>
                    <p class="m-0 text-xs text-secondary">{{ creatorMeta(creator) }}</p>
                  </div>
                  <p class="m-0 text-base font-bold tabular-nums text-success">
                    {{ creatorRoas(creator.roas) }}
                  </p>
                </li>
              }
            </ul>
          </article>
        }

        <!-- Needs you -->
        @if (alertRows().length > 0) {
          <article class="flex flex-col gap-y-3 rounded-xl border border-border bg-card p-5 shadow-sm">
            <h2 class="m-0 text-base font-semibold text-primary">Needs you</h2>

            <ul class="m-0 flex list-none flex-col gap-y-2.5 p-0">
              @for (alert of alertRows(); track alert.kind + (alert.targetId ?? '')) {
                <li
                  class="flex flex-row gap-x-3 rounded-lg border p-3.5"
                  [class]="alertToneClass(alert)"
                >
                  <div
                    aria-hidden="true"
                    class="mt-1.5 size-2 shrink-0 rounded-full"
                    [class]="alertDotClass(alert)"
                  ></div>
                  <div class="flex min-w-0 flex-col gap-y-1">
                    <p class="m-0 text-sm font-bold text-primary">{{ alert.title }}</p>
                    @if (alert.detail) {
                      <p class="m-0 text-xs text-secondary">{{ alert.detail }}</p>
                    }
                    @if (alert.actionLabel) {
                      <button
                        type="button"
                        (click)="onAlertAction(alert)"
                        class="cursor-pointer self-start text-xs font-semibold text-brand-studio hover:underline"
                      >
                        {{ alert.actionLabel }}
                      </button>
                    }
                  </div>
                </li>
              }
            </ul>
          </article>
        }
      </aside>
    </div>
  </section>
}
